import { Socket } from 'socket.io-client'
import { CommonLabelingHandlerPayload } from './types'

export default class LabelingSocket {
  private socket: Socket

  constructor(socket: Socket) {
    this.socket = socket
  }

  // ------------------- EMIT -------------------
  emitEnterLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('enter_labeling', payload)
  }

  emitMouseMove(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('mouse_move', payload)
  }

  emitCreateObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('create_object', payload)
  }

  emitSelectObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('select_object', payload)
  }

  emitDrawObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('draw_object', payload)
  }

  emitEditObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('edit_object', payload)
  }

  emitDeleteObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('delete_object', payload)
  }

  emitAddTag(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('add_tag', payload)
  }

  emitDeleteTag(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('delete_tag', payload)
  }

  emitDeleteAllObjects(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('delete_all_objects', payload)
  }

  emitChangeObjectClass(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('change_object_class', payload)
  }

  emitFinishLabelingItem(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('finish_labeling_item', payload)
  }

  emitLeaveLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('leave_labeling', payload)
  }

  emitHandleUndo(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('handle_undo', payload)
  }

  emitHandleRedo(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('handle_redo', payload)
  }

  emitCreateReviewObject(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('create_review_object', payload)
  }

  emitAddComment(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('add_comment', payload)
  }

  emitEditComment(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('edit_comment', payload)
  }

  emitDeleteComment(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('delete_comment', payload)
  }

  emitResolveComment(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('resolve_comment', payload)
  }

  emitConfirmAllComments(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('confirm_all_comments', payload)
  }

  emitStartClustering(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('start_clustering')
  }

  emitCompleteClustering(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('complete_clustering')
  }

  emitAddClass(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('add_class', payload)
  }

  emitFinishLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('finish_labeling', payload)
  }

  emitFinishReview(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('finish_review', payload)
  }

  emitRestartLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('restart_labeling', payload)
  }

  emitRestartReview(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('restart_review', payload)
  }

  emitStartLabelAssistant(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('start_label_assistant', payload)
  }

  emitFinishLabelAssistant(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('finish_label_assistant', payload)
  }

  emitLabelAssistantPromptChanged(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('change_label_assistant_prompt', payload)
  }

  emitStartSemiAutoLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('start_semi_auto_labeling', payload)
  }

  emitUpdateSemiAutoReport(payload: CommonLabelingHandlerPayload & { reportId: string }) {
    this.socket.emit('update_semi_auto_report', payload)
  }

  emitStartConditionLabeling(payload: CommonLabelingHandlerPayload) {
    this.socket.emit('start_condition_labeling', payload)
  }

  // ------------------- RECEIVE -------------------
  onIntoNewOne(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('enter_labeling', callback)
  }

  onUpdateMousePosition(callback: (data: { userName: string, userId: string, position: any }) => void): void {
    this.socket.on('update_mouse', callback)
  }

  onObjectCreated(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_created', callback)
  }

  onObjectSelected(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_selected', callback)
  }

  onObjectDrawing(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_drawing', callback)
  }

  onObjectEdited(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_edited', callback)
  }

  onObjectDeleted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_deleted', callback)
  }

  onAllObjectDeleted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('all_object_deleted', callback)
  }

  onObjectClassChanged(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('object_class_changed', callback)
  }

  onLabelingItemFinished(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('labeling_item_finished', callback)
  }

  onLeaveSomeOne(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('leave_labeling', callback)
  }

  onUndo(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('undo', callback)
  }

  onRedo(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('redo', callback)
  }

  onReviewObjectCreated(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('review_object_created', callback)
  }

  onCommentAdded(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('comment_added', callback)
  }

  onCommentEdited(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('comment_edited', callback)
  }

  onCommentDeleted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('comment_deleted', callback)
  }

  onCommentResolved(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('comment_resolved', callback)
  }

  onAllCommentsConfirmed(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('all_comments_confirmed', callback)
  }

  onClusteringStarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('clustering_started', callback)
  }

  onClusteringCompleted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('clustering_completed', callback)
  }

  onClassAdded(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('class_added', callback)
  }

  onLabelingRestarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('labeling_restarted', callback)
  }

  onLabelingFinished(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('labeling_finished', callback)
  }

  onReviewRestarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('review_restarted', callback)
  }

  onReviewFinished(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('review_finished', callback)
  }

  onLabelAssistantStarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('label_assistant_started', callback)
  }

  onLabelAssistantFinished(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('label_assistant_finished', callback)
  }

  onLabelAssistantPromptChanged(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('label_assistant_prompt_changed', callback)
  }

  onSemiAutoLabelingStarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('semi_auto_labeling_started', callback)
  }

  onSemiAutoReportUpdated(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('semi_auto_report_updated', callback)
  }

  onTagAdded(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('tag_added', callback)
  }

  onTagDeleted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('tag_deleted', callback)
  }

  onConditionLabelingStarted(callback: (data: CommonLabelingHandlerPayload) => void): void {
    this.socket.on('condition_labeling_started', callback)
  }
}
